import { useState } from 'react';
import { Settings, X, Plus, Minus, Loader2, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Analysis } from '../types';

interface ExpandAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExpand: (settings: { plusCount: number; minusCount: number }) => void;
  isLoading?: boolean;
  currentAnalysis?: Analysis; // Добавляем текущий анализ для проверки ограничений
}

export default function ExpandAnalysisModal({ 
  isOpen, 
  onClose, 
  onExpand, 
  isLoading = false,
  currentAnalysis
}: ExpandAnalysisModalProps) {
  const [plusCount, setPlusCount] = useState(2);
  const [minusCount, setMinusCount] = useState(2);

  // Получаем текущее количество аргументов
  const currentPlusCount = currentAnalysis?.items.filter(item => item.category === 'plus').length || 0;
  const currentMinusCount = currentAnalysis?.items.filter(item => item.category === 'minus').length || 0;

  // Вычисляем максимально возможное количество для генерации
  const maxPlusToGenerate = Math.max(0, 15 - currentPlusCount);
  const maxMinusToGenerate = Math.max(0, 15 - currentMinusCount);

  // Проверяем, можно ли генерировать аргументы
  const canGeneratePlus = maxPlusToGenerate > 0;
  const canGenerateMinus = maxMinusToGenerate > 0;

  // Ограничиваем значения в соответствии с доступными слотами
  const actualPlusCount = Math.min(plusCount, maxPlusToGenerate);
  const actualMinusCount = Math.min(minusCount, maxMinusToGenerate);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Проверяем, что есть что генерировать
    if (!canGeneratePlus && !canGenerateMinus) {
      return;
    }
    
    // Проверяем, что не пытаемся добавить 0 плюсов и 0 минусов одновременно
    if (actualPlusCount === 0 && actualMinusCount === 0) {
      return;
    }
    
    onExpand({
      plusCount: actualPlusCount,
      minusCount: actualMinusCount
    });
  };

  const handleClose = () => {
    if (!isLoading) {
      setPlusCount(2);
      setMinusCount(2);
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 min-h-screen min-w-screen bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center space-x-2">
            <Settings className="h-4 w-4 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Добавить аргументы
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-3">
          {/* Предупреждение о лимитах */}
          {(currentPlusCount >= 15 || currentMinusCount >= 15) && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Достигнут лимит аргументов</p>
                  <p className="text-xs">
                    {currentPlusCount >= 15 && `Плюсов: ${currentPlusCount}/15`}
                    {currentPlusCount >= 15 && currentMinusCount >= 15 && ', '}
                    {currentMinusCount >= 15 && `Минусов: ${currentMinusCount}/15`}
                  </p>
                  <p className="text-xs mt-1">
                    Для добавления новых аргументов удалите некоторые существующие.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Информация о текущем количестве */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Текущее количество аргументов:</p>
              <div className="flex justify-between text-xs">
                <span>Плюсов: {currentPlusCount}/15</span>
                <span>Минусов: {currentMinusCount}/15</span>
              </div>
            </div>
          </div>

          {/* Горизонтальное расположение элементов управления */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            {/* Плюсы - слева */}
            <div className="flex-1">
              <label className="flex items-center space-x-1 text-sm font-medium text-gray-700 mb-2">
                <Plus className="h-3 w-3 text-green-600" />
                <span className="text-sm sm:text-base">Плюсы</span>
                {!canGeneratePlus && (
                  <span className="text-xs text-red-600 ml-1">(лимит)</span>
                )}
              </label>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setPlusCount(Math.max(0, plusCount - 1))}
                  disabled={isLoading || plusCount <= 0 || !canGeneratePlus}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="w-8 text-center font-medium text-gray-900 text-base">
                  {canGeneratePlus ? actualPlusCount : 0}
                </span>
                <button
                  type="button"
                  onClick={() => setPlusCount(Math.min(5, plusCount + 1))}
                  disabled={isLoading || plusCount >= 5 || !canGeneratePlus || actualPlusCount >= maxPlusToGenerate}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
              {!canGeneratePlus && (
                <p className="text-xs text-red-600 mt-1">
                  Максимум 15 плюсов уже достигнут
                </p>
              )}
            </div>

            {/* Минусы - справа */}
            <div className="flex-1">
              <label className="flex items-center space-x-1 text-sm font-medium text-gray-700 mb-2">
                <Minus className="h-3 w-3 text-red-600" />
                <span className="text-sm sm:text-base">Минусы</span>
                {!canGenerateMinus && (
                  <span className="text-xs text-red-600 ml-1">(лимит)</span>
                )}
              </label>
              <div className="flex items-center space-x-2">
                                <button
                  type="button"
                  onClick={() => setMinusCount(Math.max(0, minusCount - 1))}
                  disabled={isLoading || minusCount <= 0 || !canGenerateMinus}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="w-8 text-center font-medium text-gray-900 text-base">
                  {canGenerateMinus ? actualMinusCount : 0}
                </span>
                <button
                  type="button"
                  onClick={() => setMinusCount(Math.min(5, minusCount + 1))}
                  disabled={isLoading || minusCount >= 5 || !canGenerateMinus || actualMinusCount >= maxMinusToGenerate}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
              {!canGenerateMinus && (
                <p className="text-xs text-red-600 mt-1">
                  Максимум 15 минусов уже достигнут
                </p>
              )}
            </div>
          </div>

          {/* Кнопки */}
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-3 py-2 text-base border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isLoading || (!canGeneratePlus && !canGenerateMinus) || (actualPlusCount === 0 && actualMinusCount === 0)}
              className="flex-1 px-3 py-2 text-base bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Добавляем...</span>
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3" />
                  <span>Добавить</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
